import { useColorScheme } from 'nativewind'
import { ColorValue, Platform, PlatformColor } from 'react-native'

type Result<Keys extends ColorKey[]> = {
  [K in keyof Keys]: ColorValue
}

export default function usePlatformColor<Keys extends ColorKey[]>(...colors: [...Keys]): Result<Keys> {
  if (Platform.OS === 'ios') {
    return colors.map(color => PlatformColor(color)) as Result<Keys>
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { colorScheme } = useColorScheme()

  return colors.map(color =>
    colorScheme === 'dark' ? PREDEFINED_COLORS[color].dark : PREDEFINED_COLORS[color].light
  ) as Result<Keys>
}

type ColorKey =
  | 'label'
  | 'secondaryLabel'
  | 'tertiaryLabel'
  | 'quaternaryLabel'
  | 'systemBlue'
  | 'systemRed'
  | 'systemGreen'
  | 'systemOrange'
  | 'systemBackground'
  | 'secondarySystemBackground'
  | 'systemGroupedBackgroundColor'
  | 'secondarySystemGroupedBackgroundColor'
  | 'separator'
  | 'opaqueSeparator'
  | 'link'

const PREDEFINED_COLORS: Record<ColorKey, { light: ColorValue; dark: ColorValue }> = {
  label: {
    light: '#000000',
    dark: '#FFFFFF',
  },
  secondaryLabel: {
    light: '#3C3C4399',
    dark: '#EBEBF599',
  },
  tertiaryLabel: {
    light: '#3C3C434C',
    dark: '#EBEBF54C',
  },
  quaternaryLabel: {
    light: '#3C3C432E',
    dark: '#EBEBF52E',
  },
  systemBlue: {
    light: '#007AFF',
    dark: '#0A84FF',
  },
  systemRed: {
    light: '#FF3B30',
    dark: '#FF453A',
  },
  systemGreen: {
    light: '#34C759',
    dark: '#30D158',
  },
  systemOrange: {
    light: '#FF9500',
    dark: '#FF9F0A',
  },
  systemBackground: {
    light: '#FFFFFF',
    dark: '#000000',
  },
  secondarySystemBackground: {
    light: '#F2F2F7',
    dark: '#1C1C1E',
  },
  systemGroupedBackgroundColor: {
    light: '#F2F2F7',
    dark: '#000000',
  },
  secondarySystemGroupedBackgroundColor: {
    light: '#FFFFFF',
    dark: '#1C1C1E',
  },
  separator: {
    light: '#3C3C434A',
    dark: '#54545899',
  },
  opaqueSeparator: {
    light: '#C6C6C8',
    dark: '#38383A',
  },
  link: {
    light: '#007AFF',
    dark: '#0A84FF',
  },
}
